import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EmployeeForm = ({ employee, departments, roles, statuses, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    role: '',
    jobTitle: '',
    employmentType: 'Full-time',
    manager: '',
    workLocation: '',
    salary: '',
    hireDate: '',
    status: 'Active'
  });

  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    job: true,
    compliance: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee?.name || '',
        email: employee?.email || '',
        phone: employee?.phone || '',
        department: employee?.department || '',
        role: employee?.role || '',
        jobTitle: employee?.jobTitle || '',
        employmentType: employee?.employmentType || 'Full-time',
        manager: employee?.manager || '',
        workLocation: employee?.workLocation || '',
        salary: employee?.salary || '',
        hireDate: employee?.hireDate || '',
        status: employee?.status || 'Active'
      });
    }
  }, [employee]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone is required';
    if (!formData?.department) newErrors.department = 'Department is required';
    if (!formData?.role) newErrors.role = 'Role is required';
    if (!formData?.hireDate) newErrors.hireDate = 'Hire date is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground">
          {employee ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          <Icon name="X" size={20} className="text-muted-foreground" />
        </button>
      </div>
      {/* Personal Information Section */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('personal')}
          className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon name="User" size={18} className="text-primary" />
            <span className="font-semibold text-foreground">Personal Information</span>
          </div>
          <Icon 
            name={expandedSections?.personal ? "ChevronUp" : "ChevronDown"} 
            size={18} 
            className="text-muted-foreground" 
          />
        </button>
        {expandedSections?.personal && (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Full Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                placeholder="Enter full name"
                error={errors?.name}
              />
              {errors?.name && (
                <p className="text-xs text-destructive mt-1">{errors?.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email Address <span className="text-destructive">*</span>
              </label>
              <Input
                type="email"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                placeholder="email@company.com"
                error={errors?.email}
              />
              {errors?.email && (
                <p className="text-xs text-destructive mt-1">{errors?.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Phone Number <span className="text-destructive">*</span>
              </label>
              <Input
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                placeholder="(555) 123-4567"
                error={errors?.phone}
              />
              {errors?.phone && (
                <p className="text-xs text-destructive mt-1">{errors?.phone}</p>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Job Details Section */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('job')}
          className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon name="Briefcase" size={18} className="text-primary" />
            <span className="font-semibold text-foreground">Job Details</span>
          </div>
          <Icon 
            name={expandedSections?.job ? "ChevronUp" : "ChevronDown"} 
            size={18} 
            className="text-muted-foreground" 
          />
        </button>
        {expandedSections?.job && (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Department <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.department}
                onChange={(e) => handleChange('department', e?.target?.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.department ? 'border-destructive' : 'border-border'
                }`}
              >
                <option value="">Select department</option>
                {departments?.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
              {errors?.department && (
                <p className="text-xs text-destructive mt-1">{errors?.department}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Role <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.role}
                onChange={(e) => handleChange('role', e?.target?.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.role ? 'border-destructive' : 'border-border'
                }`}
              >
                <option value="">Select role</option>
                {roles?.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              {errors?.role && (
                <p className="text-xs text-destructive mt-1">{errors?.role}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Job Title
              </label>
              <Input
                value={formData?.jobTitle}
                onChange={(e) => handleChange('jobTitle', e?.target?.value)}
                placeholder="e.g., Senior Software Engineer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Employment Type
              </label>
              <select
                value={formData?.employmentType}
                onChange={(e) => handleChange('employmentType', e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Intern">Intern</option>
                <option value="Temporary">Temporary</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Manager/Supervisor
              </label>
              <Input
                value={formData?.manager}
                onChange={(e) => handleChange('manager', e?.target?.value)}
                placeholder="Enter manager name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Work Location
              </label>
              <Input
                value={formData?.workLocation}
                onChange={(e) => handleChange('workLocation', e?.target?.value)}
                placeholder="e.g., New York Office, Remote"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Annual Salary
              </label>
              <Input
                type="number"
                value={formData?.salary}
                onChange={(e) => handleChange('salary', e?.target?.value)}
                placeholder="e.g., 75000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Hire Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="date"
                value={formData?.hireDate}
                onChange={(e) => handleChange('hireDate', e?.target?.value)}
                error={errors?.hireDate}
              />
              {errors?.hireDate && (
                <p className="text-xs text-destructive mt-1">{errors?.hireDate}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Employment Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleChange('status', e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {statuses?.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      {/* Compliance Setup Section */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('compliance')}
          className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon name="public/assets/images/Real_logo-1771587686730.png" size={18} className="text-primary" />
            <span className="font-semibold text-foreground">Initial Compliance Setup</span>
          </div>
          <Icon 
            name={expandedSections?.compliance ? "ChevronUp" : "ChevronDown"} 
            size={18} 
            className="text-muted-foreground" 
          />
        </button>
        {expandedSections?.compliance && (
          <div className="p-4">
            <p className="text-sm text-muted-foreground">
              Compliance tracking will be automatically initialized with default settings. 
              You can configure specific compliance rules after creating the employee record.
            </p>
          </div>
        )}
      </div>
      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
        >
          {employee ? 'Update Employee' : 'Add Employee'}
        </Button>
      </div>
    </form>
  );
};

export default EmployeeForm;